import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, inject } from '@angular/core';
import { IProductDataV3 } from '@df/catalog/product/product';
import { StickyPanelDirective } from '@df/legacy/ui/sticky/sticky-panel.directive';
import { ProductConfiguration } from '@df/module-product-configuration/model/product-configuration';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { Product } from '../../product.decorator';
import { ProductViewDetailsLeftComponent } from '../details/left/product-view-details-left.component';
import { ProductViewDetailsNameComponent } from '../details/name/product-view-details-name.component';
import { ProductViewDetailsComponent } from '../details/product-view-details.component';
import { ProductViewMediaComponent } from '../media/product-view-media.component';
import { ProductViewComponent } from '../product-view.component';
import { ProductViewRelatedCarouselComponent } from '../related-carousel/product-view-related-carousel.component';
import { ProductViewProp65WarningComponent } from '../prop65-warning/product-view-prop65-warning.component';
import { NProductViewLayout } from './product-view-layout.namespace';
import { ProductViewComplexService } from '../product-view-complex.service';
import { CommonModule } from '@angular/common';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { ICmsOutletBlockComponent } from '../../../outlet-block/icms-outlet-block.component';

export interface IProductViewDisplayColourPopupEvent {
    element: HTMLElement;
    to: boolean;
    type: 'image' | 'background';
    swatch: string;
    title?: string;
    subtitle?: string;
}

@ICmsComponent<ProductViewLayoutComponent>({
    id: NProductViewLayout.BLOCK_ID,
    block: {
        url: NProductViewLayout.BLOCK_ID
    },
    onDemandOnly: true,
    name: 'Product page layout',
    description: 'Product page layout block',
    fields: [
        <NICmsContentField.IComponentsField>{
            key: 'bottomComponents',
            type: NICmsContentField.EFieldType.components,
            title: 'Bottom components',
            itemName: 'component',
            default: []
        },
        {
            key: 'technical-label',
            type: NICmsContentField.EFieldType.label,
            title: 'Technical Information'
        },
        <NICmsContentField.IRelationField>{
            key: 'attributes',
            type: NICmsContentField.EFieldType.relation,
            title: 'Attributes',
            options: {
                type: 'attribute',
                multiple: true
            }
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'sectionGetSample',
            title: 'Get sample section'
        },
        {
            key: 'getSampleUrl',
            type: NICmsContentField.EFieldType.text,
            title: 'Get Sample Url'
        },
        {
            key: 'getSampleTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Get Sample Title',
            default: 'Get a sample!'
        },
        {
            key: 'getSampleSubtitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Get Sample Subtitle'
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'sectionTalkToExpert',
            title: 'Talk to an expert section'
        },
        <NICmsContentField.IField>{
            key: 'talkToExpertImage',
            type: NICmsContentField.EFieldType.image,
            title: 'Image'
        },
        <NICmsContentField.IField>{
            key: 'talkToExpertUrl',
            type: NICmsContentField.EFieldType.text,
            title: 'Url',
            description: 'Link will be applied to the whole element'
        },
        <NICmsContentField.ITextField>{
            key: 'talkToExpertTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Title'
        },
        <NICmsContentField.ITextField>{
            key: 'talkToExpertDescription',
            type: NICmsContentField.EFieldType.text,
            title: 'Description',
            default: 'Need help? Give us a call!'
        },

        {
            type: NICmsContentField.EFieldType.label,
            key: 'sectionPriceList',
            title: 'Price list section'
        },
        <NICmsContentField.ITextField>{
            key: 'priceListDescription',
            type: NICmsContentField.EFieldType.text,
            title: 'Price List Description'
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'additionalLeadTimeLabel',
            title: 'Additional Lead Time Section'
        },
        <NICmsContentField.ITextField>{
            key: 'additionalLeadTimeText',
            type: NICmsContentField.EFieldType.text,
            title: 'Additional Lead Time Text'
        },
        <NICmsContentField.IField>{
            key: 'truspilotSettings',
            type: NICmsContentField.EFieldType.label,
            title: 'Truspilot settings'
        },
        <NICmsContentField.IField>{
            key: 'trustpilotBusinessunitId',
            type: NICmsContentField.EFieldType.text,
            title: 'TrustPilot Business Unit Id'
        },
        <NICmsContentField.IField>{
            key: 'trustpilotMiniTemplateId',
            type: NICmsContentField.EFieldType.text,
            title: 'TrustPilot Product Mini Widget Template Unit Id'
        }
    ]
})
@Component({
    standalone: true,
    imports: [
        ProductViewDetailsComponent,
        ProductViewMediaComponent,
        ICmsClientModule,
        StickyPanelDirective,
        ProductViewDetailsNameComponent,
        ProductViewDetailsLeftComponent,
        ProductViewRelatedCarouselComponent,
        ProductViewProp65WarningComponent,
        ICmsOutletBlockComponent,
        CommonModule,
        DfModuleImageModule,
        TranslationModule
    ],
    selector: 'cms-product-view-layout',
    templateUrl: './product-view-layout.html',
    styleUrl: './product-view-layout.scss'
})
export class ProductViewLayoutComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit, OnChanges, OnDestroy {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NProductViewLayout.IBlockDataContent;

    @Input()
    variant?: string;

    protected override canDetach = false;

    @Input({
        required: true
    })
    product!: Product<IProductDataV3>;

    /**
     * Parent product shorthand
     */
    @Input({
        required: true
    })
    parent: Product<IProductDataV3> | undefined;

    colourPopupEvent: IProductViewDisplayColourPopupEvent | undefined = undefined;

    colourPopupStyle;

    // even if not used it is needed to initialize before component renders
    private productConfiguration = inject(ProductConfiguration);

    readonly productView = inject(ProductViewComponent);

    private productViewComplexService = inject(ProductViewComplexService);

    private elementRef = inject(ElementRef);

    override ngOnInit(): void {
        super.ngOnInit();
        if (!this.isComplexConfigurable) {
            this.productViewComplexService.product = undefined;
        }
        if (!this.isComplexConfigurable || !this.productConfiguration.hasCustomOptions) {
            this.productViewComplexService.customOptions = undefined;
        }
    }

    private get isComplexConfigurable(): boolean {
        const product = this.productConfiguration.product$.value as Product;
        return product.isComplexPanel && this.productConfiguration.type === 'configurable';
    }

    override detectChanges() {
        this.client.isCms ? this.productView.detectChanges() : super.detectChanges();
    }

    protected get isSM(): boolean {
        return this.client.sizeId < 2;
    }

    displayColourPopup(event: IProductViewDisplayColourPopupEvent) {
        if (event.to && this.nativeElement) {
            const rect = this.nativeElement.getBoundingClientRect();
            const sourceRect = event.element.getBoundingClientRect();
            const top = sourceRect.top - rect.top - 80;
            const left = sourceRect.left - rect.left - 200 - 100;
            const popupStyle = { top: (top > 0 ? top : 0) + 'px', left: (left > 0 ? left : 0) + 'px' };
            this.colourPopupStyle = popupStyle;
            this.colourPopupEvent = event;
        } else {
            this.colourPopupEvent = undefined;
        }
        this.colourPopupEvent = event.to ? event : undefined;
        this.detectChanges();
    }

    private get nativeElement(): HTMLElement {
        return this.elementRef.nativeElement;
    }
}
